import type {
  AltitudeRef,
  Track,
  TrackPoints,
  TrackSourceKind,
  TrackWarning,
} from '@/lib/db/types';

/**
 * What a parser produces, before normalisation.
 *
 * `NaN` is the one and only way to say "this source did not carry that value".
 * It matches the `Float32Array` columns in `TrackPoints`, so normalisation
 * copies rather than branches, and an absent altitude can never be mistaken for
 * sea level — which is the bug that makes a whole flight look like it taxied.
 */
export interface ParsedPoint {
  /** Epoch milliseconds, or `NaN` where the source has no time at all. */
  tMs: number;
  lat: number;
  lon: number;
  altFt: number;
  /** Groundspeed. ADS-B does not carry airspeed — never relabel this. */
  gsKt: number;
  trackDeg: number;
}

/**
 * A parser's whole output: the points plus whatever identity the file happened
 * to carry. Nothing here is trusted — normalisation validates it.
 */
export interface ParsedTrack {
  source: TrackSourceKind;
  points: ParsedPoint[];
  altitudeRef: AltitudeRef;
  callsign?: string | undefined;
  registration?: string | undefined;
  icaoHex?: string | undefined;
  /** Warnings the parser can see that normalisation cannot, e.g. a lost layout. */
  warnings: TrackWarning[];
  /**
   * What was actually recognised, in the pilot's words: "Route folder, 297
   * points" or "columns: UTC, Position, Altitude". Shown in the import result
   * list, because "it worked" is not the same as "it read what you expected".
   */
  notes: string[];
}

/** Where a file came from, kept so a parser bug is recoverable by re-import. */
export interface Provenance {
  /** The filename as dropped, or an upstream id such as an `fr24_id`. */
  sourceRef: string;
  /** Overridable so a test is not a moving target. */
  importedAt?: string | undefined;
  /** Overridable so ids are stable in tests. */
  id?: string | undefined;
}

/** A normalised track, ready to persist. */
export interface NormalisedTrack {
  track: Track;
  points: TrackPoints;
}

/** Every format this build can read. */
export type TrackFormat = 'fr24-csv' | 'fr24-kml' | 'kmz' | 'gpx' | 'igc' | 'geojson';

/**
 * A file parser.
 *
 * Parsers are synchronous and take bytes: that keeps them pure, trivially
 * testable, and runnable inside a worker without any of them knowing there is
 * one. `kmz` is the one format that needs to unzip first, and it does that
 * synchronously too.
 */
export interface FormatParser {
  format: TrackFormat;
  label: string;
  /** Extensions this parser claims, lowercase, with the dot. */
  extensions: readonly string[];
  /**
   * Whether this parser recognises the content. Called only after extension
   * matching fails or is ambiguous, so it can afford to be strict.
   */
  sniff(text: string): boolean;
  parse(bytes: Uint8Array): ParsedTrack;
}

/** Thrown when a file cannot be read as a track. The message is shown to the pilot. */
export class ParseError extends Error {
  /** Structured detail for the import result list, e.g. unrecognised columns. */
  readonly detail?: string[] | undefined;

  constructor(message: string, detail?: string[]) {
    super(message);
    this.name = 'ParseError';
    this.detail = detail;
  }
}

export type { AltitudeRef, Track, TrackPoints, TrackSourceKind, TrackWarning };
